import { useState, useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Search, Trash2, User, Bell, HelpCircle, X, ChevronLeft, ChevronRight, Share2, CheckCircle2, Clock, Download, FileText, Database } from "lucide-react"
import { DashboardSidebar } from "@/components/dashboard-sidebar"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { TransferHistoryList } from "@/components/transfer-history-list"
import { NAVIGATION_ROUTES } from '@/constants/routes'
import { useToast } from "@/hooks/use-toast"
import { 
  getTransferHistory,
  getTransferStatistics,
  deleteTransfer,
  revokeTransfer,
  formatFileSize,
  type TransferHistoryItem,
  type TransferStatistics
} from '@/api/history'

export default function History() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [transfers, setTransfers] = useState<TransferHistoryItem[]>([])
  const [statistics, setStatistics] = useState<TransferStatistics | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'burned'>('all')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<string | null>(null)
  const pageSize = 10

  // 加载数据与统计
  useEffect(() => {
    loadData()
  }, [page])

  const loadData = async () => {
    setLoading(true)
    try {
      const [historyData, stats] = await Promise.all([
        getTransferHistory(page, pageSize),
        getTransferStatistics().catch(() => null)
      ])
      setTransfers(historyData.items)
      setTotalPages(historyData.totalPages || 1)
      setTotal(historyData.total || 0)
      if (stats) {
        setStatistics(stats)
      }
    } catch (error: any) {
      console.error('加载传输历史失败:', error)
      if (error.message.includes('未登录')) {
        navigate(NAVIGATION_ROUTES.HOME)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked)
    if (checked) {
      // 全选所有项
      setSelectedIds(transfers.map(t => t.id))
    } else {
      setSelectedIds([])
    }
  }

  // 打开批量删除确认弹窗
  const handleOpenBatchDelete = () => {
    if (selectedIds.length === 0) return
    setItemToDelete(null)
    setDeleteDialogOpen(true)
  }

  // 打开单个删除确认弹窗
  const handleOpenSingleDelete = (id: string) => {
    setItemToDelete(id)
    setDeleteDialogOpen(true)
  }

  // 执行删除确认
  const handleConfirmDelete = async () => {
    try {
      if (itemToDelete) {
        await deleteTransfer(itemToDelete)
        toast({ title: "删除成功", description: "传输记录已删除" })
      } else {
        await Promise.all(selectedIds.map(id => deleteTransfer(id)))
        toast({ title: "删除成功", description: `已删除 ${selectedIds.length} 条记录` })
        setSelectedIds([])
      }
      loadData()
    } catch (error: any) {
      toast({ title: "删除失败", description: error.message || '操作失败', variant: "destructive" })
    } finally {
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }

  const handleRevoke = async (id: string) => {
    try {
      await revokeTransfer(id)
      toast({ title: "撤销成功", description: "该分享已被撤销" })
      loadData()
    } catch (error: any) {
      toast({ title: "撤销失败", description: error.message || '撤销失败', variant: "destructive" })
    }
  }

  const toggleSort = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc")
  }

  // 状态过滤
  const filteredTransfers = useMemo(() => {
    if (statusFilter === 'all') return transfers
    if (statusFilter === 'active') return transfers.filter(t => t.isActive && !t.isExpired)
    if (statusFilter === 'expired') return transfers.filter(t => t.isExpired)
    if (statusFilter === 'burned') return transfers.filter(t => t.burnAfterRead || t.status === 'burned')
    return transfers
  }, [transfers, statusFilter])

  // 自动更新全选状态
  useEffect(() => {
    if (selectedIds.length === transfers.length && transfers.length > 0) {
      setSelectAll(true)
    } else {
      setSelectAll(false)
    }
  }, [selectedIds, transfers])

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* 左侧边栏 */}
      <DashboardSidebar activePage="history" />

      {/* 右侧内容区 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部标题栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            {/* 左侧标题 */}
            <h1 className="text-xl font-bold text-gray-900">传输历史</h1>

            {/* 中间搜索框 */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="搜索" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 rounded-lg" 
                />
              </div>
            </div>

            {/* 右侧操作按钮 */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900 h-auto p-0 text-sm">
                升级会员
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                <HelpCircle className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 relative h-8 w-8">
                <Bell className="w-4 h-4" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
              <Link to={NAVIGATION_ROUTES.SETTINGS}>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-gray-600 hover:text-gray-900 h-8 w-8"
                >
                  <User className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={NAVIGATION_ROUTES.HOME}>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* 次级工具栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-3">
          <div className="flex items-center justify-between">
            {/* 左侧：状态筛选 */}
            <div className="flex items-center gap-1.5 bg-gray-100/70 p-1 rounded-lg">
              <Button
                variant={statusFilter === 'all' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 px-2.5 text-xs font-medium rounded-md ${statusFilter === 'all' ? 'bg-white text-gray-900 shadow-sm hover:bg-white' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setStatusFilter('all')}
              >
                全部
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 px-2.5 text-xs font-medium rounded-md ${statusFilter === 'active' ? 'bg-white text-emerald-700 shadow-sm hover:bg-white' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setStatusFilter('active')}
              >
                活跃中
              </Button>
              <Button
                variant={statusFilter === 'expired' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 px-2.5 text-xs font-medium rounded-md ${statusFilter === 'expired' ? 'bg-white text-amber-700 shadow-sm hover:bg-white' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setStatusFilter('expired')}
              >
                已过期
              </Button>
              <Button
                variant={statusFilter === 'burned' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 px-2.5 text-xs font-medium rounded-md ${statusFilter === 'burned' ? 'bg-white text-purple-700 shadow-sm hover:bg-white' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setStatusFilter('burned')}
              >
                阅后即焚
              </Button>
            </div>

            {/* 右侧：排序和操作 */}
            <div className="flex items-center gap-3">
              {selectedIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-3 text-sm"
                  onClick={handleOpenBatchDelete}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  全部删除 ({selectedIds.length})
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-600 hover:text-gray-900 h-8"
                onClick={toggleSort}
              >
                <span className="text-sm">上传时间</span>
                {sortOrder === "asc" ? (
                  <span className="ml-1 text-xs">↑</span>
                ) : (
                  <span className="ml-1 text-xs">↓</span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* 主内容区 */}
        <main className="flex-1 bg-gray-50 p-8">
          <div className="w-full space-y-4">
            {/* 统计指标卡片 */}
            {statistics && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>总分享数</span>
                    <Share2 className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                  <div className="text-xl font-bold text-gray-900">{statistics.totalShares}</div>
                </Card>
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-emerald-600 mb-1">
                    <span>活跃分享</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  </div>
                  <div className="text-xl font-bold text-emerald-600">{statistics.activeShares}</div>
                </Card>
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-amber-600 mb-1">
                    <span>已过期</span>
                    <Clock className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                  <div className="text-xl font-bold text-amber-600">{statistics.expiredShares}</div>
                </Card>
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-blue-600 mb-1">
                    <span>总下载次数</span>
                    <Download className="w-3.5 h-3.5 text-blue-500" />
                  </div>
                  <div className="text-xl font-bold text-blue-600">{statistics.totalDownloads}</div>
                </Card>
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-purple-600 mb-1">
                    <span>总文件数</span>
                    <FileText className="w-3.5 h-3.5 text-purple-500" />
                  </div>
                  <div className="text-xl font-bold text-purple-600">{statistics.totalFiles}</div>
                </Card>
                <Card className="p-3.5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-center justify-between text-xs text-cyan-600 mb-1">
                    <span>累计传输</span>
                    <Database className="w-3.5 h-3.5 text-cyan-500" />
                  </div>
                  <div className="text-xl font-bold text-cyan-600">{formatFileSize(statistics.totalSize)}</div>
                </Card>
              </div>
            )}

            {/* 列表 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* 表头 */}
              <div className="grid grid-cols-[auto_1fr_200px_120px] gap-4 px-6 py-4 border-b border-gray-200 text-sm font-medium text-gray-600 bg-gray-50">
                <div className="flex items-center">
                  <Checkbox 
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span>传输历史</span>
                  {selectedIds.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2 text-xs"
                      onClick={handleOpenBatchDelete}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      全部删除
                    </Button>
                  )}
                </div>
                <div>传输状态</div>
                <div className="text-right">操作</div>
              </div>

              {/* 列表内容 */}
              {loading ? (
                <div className="text-center py-12 text-gray-500">加载中...</div>
              ) : filteredTransfers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {transfers.length === 0 ? "暂无传输记录" : "未找到符合条件的传输记录"}
                </div>
              ) : (
                <TransferHistoryList 
                  searchQuery={searchQuery}
                  sortOrder={sortOrder}
                  selectedIds={selectedIds}
                  onSelectChange={setSelectedIds}
                  transfers={filteredTransfers}
                  onDelete={handleOpenSingleDelete}
                  onRevoke={handleRevoke}
                />
              )}
            </div>

            {/* 分页组件 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  共 {total} 项，第 {page} / {totalPages} 页
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page > 1) {
                        setPage(page - 1)
                      }
                    }}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    上一页
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (page <= 3) {
                        pageNum = i + 1
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = page - 2 + i
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          disabled={loading}
                          className="w-9"
                        >
                          {pageNum}
                        </Button>
                      )
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page < totalPages) {
                        setPage(page + 1)
                      }
                    }}
                    disabled={page === totalPages || loading}
                  >
                    下一页
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
            {totalPages <= 1 && total > 0 && (
              <div className="text-center text-sm text-gray-500 py-4">
                共 {total} 项
              </div>
            )}
          </div>
        </main>

        {/* 规范删除确认对话框 */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>确认删除传输记录？</AlertDialogTitle>
              <AlertDialogDescription>
                {itemToDelete
                  ? "确定要删除这条传输记录吗？删除后对方将无法通过此链接下载。"
                  : `确定要删除选中的 ${selectedIds.length} 条传输记录吗？删除后对方将无法通过这些链接下载。`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                确认删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
